import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FaCalendarAlt, FaChartBar, FaCompress, FaExpand, FaFilter, FaInfoCircle, FaMapMarkerAlt, FaSitemap } from 'react-icons/fa';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell, LabelList,
  Legend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis, YAxis
} from 'recharts';

function FactoryAuditProgress() {
  const [auditData, setAuditData] = useState({
    audits: [],
    branchData: [],
    departmentData: []
  });
  const [filterOptions, setFilterOptions] = useState({
    branches: [],
    departments: [],
    dateRange: {}
  });
  const [filters, setFilters] = useState({
    branches: '',
    departments: '',
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({});
  const [viewMode, setViewMode] = useState('branch'); // 'branch' or 'department'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  const BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData || !userData.FactoryCode) {
          throw new Error('User data not found');
        }

        const response = await fetch(`${BASE_URL}/get-factory-audit-progress-filters/${userData.FactoryCode}`);
        if (!response.ok) {
          throw new Error('Failed to fetch filter options');
        }

        const data = await response.json();
        if (data.success) {
          setFilterOptions(data.filters);
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
        setError(err.message);
      }
    };

    fetchFilterOptions();
  }, [BASE_URL]);

  useEffect(() => {
    const fetchAuditProgress = async () => {
      try {
        setLoading(true);
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData || !userData.FactoryCode) {
          throw new Error('User data not found');
        }

        // Build query parameters
        const params = new URLSearchParams();
        if (filters.branches) params.append('branches', filters.branches);
        if (filters.departments) params.append('departments', filters.departments);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        const response = await fetch(
          `${BASE_URL}/get-factory-audit-progress/${userData.FactoryCode}?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch audit progress data');
        }

        const data = await response.json();
        if (data.success) {
          setAuditData({
            audits: data.data.audits || [],
            branchData: data.data.branchData || [],
            departmentData: data.data.departmentData || []
          });
          setSummary(data.data.summary || {});
          
          // Auto-switch to department view if department filter is applied
          if (filters.departments) {
            setViewMode('department');
          } else if (!filters.departments && viewMode === 'department') {
            setViewMode('branch');
          }
        } else {
          throw new Error(data.error || 'Failed to fetch audit progress data');
        }
      } catch (err) {
        console.error('Error fetching audit progress:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditProgress();
  }, [BASE_URL, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilter = (filterType) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: ''
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      branches: '',
      departments: '',
      startDate: '',
      endDate: ''
    });
    setViewMode('branch');
  };

  // Fullscreen functionality
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.error('Error entering fullscreen:', err);
      }
    } else {
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch (err) {
        console.error('Error exiting fullscreen:', err);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Check if filters are active
  const hasActiveFilters = filters.branches || 
                          filters.departments || 
                          filters.startDate || 
                          filters.endDate;

  // Get current display data based on view mode and filters
  const getCurrentDisplayData = () => {
    if (viewMode === 'department') {
      // If a specific department is filtered, show only that department's data
      if (filters.departments) {
        return auditData.departmentData.filter(dept => dept.departmentName === filters.departments);
      }
      return auditData.departmentData;
    }
    return auditData.branchData;
  };

  // Prepare chart data
  const currentData = getCurrentDisplayData();
  
  // Get color based on score
  const getScoreColor = (score) => {
    if (score >= 80) return '#28a745'; // green
    if (score >= 60) return '#ffc107'; // yellow
    return '#dc3545'; // red
  };

  // Custom tooltip for recharts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
        }}>
          <p className="fw-bold mb-1" style={{ color: '#344767' }}>
            {viewMode === 'department' ? data.departmentName : data.branchName}
          </p>
          <p className="mb-1" style={{ 
            color: getScoreColor(data.avgScore),
            fontWeight: 'bold'
          }}>
            Score: {data.avgScore}%
          </p>
          <p className="mb-1" style={{ color: '#6c757d' }}>Total Audits: {data.totalAudits}</p>
          {viewMode === 'department' && (
            <p className="mb-0" style={{ color: '#6c757d' }}>
              Total Score: {data.totalScore}/{data.totalPossibleScore}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm mb-4 rounded-3 overflow-hidden">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading audit progress data...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-sm mb-4 rounded-3 overflow-hidden">
        <Card.Body>
          <Alert variant="danger">
            <Alert.Heading>Error Loading Audit Progress</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div ref={containerRef} className={isFullscreen ? 'fullscreen-container' : ''}>
      <style jsx>{`
        .fullscreen-container {
          background: white;
          padding: 20px;
          height: 100vh;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }
        .fullscreen-chart {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .fullscreen-chart .card {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .fullscreen-chart .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .fullscreen-chart .chart-container {
          flex: 1;
          min-height: 0;
        }
      `}</style>
      
      {isFullscreen ? (
        // Fullscreen chart view only
        <div className="fullscreen-chart">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0 d-flex align-items-center">
              <div className="icon-wrapper bg-primary bg-opacity-10 p-2 rounded-circle me-2 text-primary">
                <FaChartBar />
              </div>
              <span>
                {filters.departments 
                  ? `${filters.departments} Department - Audit Score Performance`
                  : viewMode === 'department' 
                    ? 'Department-wise Audit Score Performance' 
                    : 'Branch-wise Audit Score Performance'}
              </span>
            </h5>
            <button
              className="btn btn-outline-primary btn-sm d-flex align-items-center"
              onClick={toggleFullscreen}
              title="Exit Fullscreen"
            >
              <FaCompress className="me-1" />
              Exit Fullscreen
            </button>
          </div>
          
          <Card className="border-0 shadow-sm rounded-3 overflow-hidden flex-fill">
            <Card.Body className="d-flex flex-column">
              {currentData.length > 0 ? (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={currentData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        dataKey={viewMode === 'department' ? "departmentName" : "branchName"} 
                        angle={-45} 
                        textAnchor="end"
                        height={70}
                        tick={{ fontSize: 12, fill: '#6c757d' }}
                        tickLine={false}
                      />
                      <YAxis 
                        domain={[0, 100]}
                        tickFormatter={(value) => `${value}%`}
                        tick={{ fontSize: 12, fill: '#6c757d' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: 15 }} />
                      <Bar 
                        name="Average Score (%)" 
                        dataKey="avgScore" 
                        barSize={100}
                        animationDuration={1500}
                        animationEasing="ease-out"
                      >
                        {
                          currentData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={getScoreColor(entry.avgScore)}
                              stroke={getScoreColor(entry.avgScore)}
                              strokeWidth={1}
                            />
                          ))
                        }
                        <LabelList 
                          dataKey="avgScore" 
                          position="top" 
                          formatter={(value) => `${value}%`}
                          style={{ fontSize: '12px', fill: '#344767', fontWeight: 'bold' }} 
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-5 flex-fill d-flex align-items-center justify-content-center">
                  <div>
                    <div className="mb-3 bg-light p-3 rounded-circle d-inline-flex align-items-center justify-content-center">
                      <FaChartBar size={32} className="text-secondary" />
                    </div>
                    <h6 className="text-muted">No audit progress data available</h6>
                    <p className="text-muted small">
                      {hasActiveFilters
                        ? 'No data found for the selected filters.'
                        : 'Complete some audits to see progress analytics here.'}
                    </p>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      ) : (
        // Normal view with all components
        <Row className="g-4 mb-4">
          <Col xs={12}>
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="section-title mb-3 d-flex align-items-center">
                <div className="icon-wrapper bg-primary bg-opacity-10 p-2 rounded-circle me-2 text-primary">
                  <FaChartBar />
                </div>
                <span>Audit Progress Analytics</span>
              </h5>
              <button
                className="btn btn-outline-primary btn-sm d-flex align-items-center"
                onClick={toggleFullscreen}
                title="Enter Fullscreen"
              >
                <FaExpand className="me-1" />
                Fullscreen
              </button>
            </div>
          </Col>

          {/* Filters and View Toggle */}
          <Col xs={12}>
            <Card className="border-0 shadow-sm mb-3 rounded-3 overflow-hidden">
              <Card.Header className="bg-white border-bottom-0 py-3">
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                  <div className="d-flex align-items-center mb-2 mb-md-0">
                    <div className="bg-primary bg-opacity-10 p-2 rounded-circle me-2 text-primary">
                      <FaFilter />
                    </div>
                    <h6 className="mb-0 fw-bold">Filters</h6>
                  </div>
                  <div className="d-flex gap-2">
                    {/* View Mode Toggle */}
                    <div className="btn-group" role="group">
                      <input
                        type="radio"
                        className="btn-check"
                        name="viewMode"
                        id="branchView"
                        checked={viewMode === 'branch'}
                        onChange={() => setViewMode('branch')}
                        disabled={filters.departments}
                      />
                      <label className="btn btn-outline-primary btn-sm" htmlFor="branchView">
                        <FaMapMarkerAlt className="me-1" /> Branches
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="viewMode"
                        id="departmentView"
                        checked={viewMode === 'department'}
                        onChange={() => setViewMode('department')}
                      />
                      <label className="btn btn-outline-success btn-sm" htmlFor="departmentView">
                        <FaSitemap className="me-1" /> Departments
                      </label>
                    </div>

                    {hasActiveFilters && (
                      <button 
                        className="btn btn-light btn-sm d-flex align-items-center"
                        onClick={clearAllFilters}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="pt-0">
                <Row className="g-3">
                  <Col xs={12} md={3}>
                    <Form.Group className="position-relative">
                      <Form.Label className="small fw-semibold text-muted d-flex align-items-center">
                        <FaMapMarkerAlt className="me-1" /> Branch
                      </Form.Label>
                      <Form.Select
                        size="sm"
                        value={filters.branches}
                        onChange={(e) => handleFilterChange('branches', e.target.value)}
                        className="border-0 bg-light py-2"
                      >
                        <option value="">All Branches</option>
                        {filterOptions.branches.map(branch => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-muted d-flex align-items-center">
                        <FaSitemap className="me-1" /> Department
                      </Form.Label>
                      <Form.Select
                        size="sm"
                        value={filters.departments}
                        onChange={(e) => handleFilterChange('departments', e.target.value)}
                        className="border-0 bg-light py-2"
                      >
                        <option value="">All Departments</option>
                        {filterOptions.departments.map(dept => (
                          <option key={dept.id} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-muted d-flex align-items-center">
                        <FaCalendarAlt className="me-1" /> Start Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        className="border-0 bg-light"
                        placeholder="Start Date"
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-muted d-flex align-items-center">
                        <FaCalendarAlt className="me-1" /> End Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        className="border-0 bg-light"
                        placeholder="End Date"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {hasActiveFilters && (
                  <div className="mt-3">
                    <div className="d-flex flex-wrap gap-2">
                      {filters.branches && (
                        <Badge bg="primary" className="d-flex align-items-center py-2 px-3 rounded-pill">
                          Branch: {filterOptions.branches.find(b => b.id == filters.branches)?.name}
                          <button 
                            className="btn-close btn-close-white ms-2" 
                            style={{ fontSize: '0.6em' }}
                            onClick={() => clearFilter('branches')}
                            aria-label="Remove branch filter"
                          ></button>
                        </Badge>
                      )}
                      {filters.departments && (
                        <Badge bg="success" className="d-flex align-items-center py-2 px-3 rounded-pill">
                          Department: {filters.departments}
                          <button 
                            className="btn-close btn-close-white ms-2" 
                            style={{ fontSize: '0.6em' }}
                            onClick={() => clearFilter('departments')}
                            aria-label="Remove department filter"
                          ></button>
                        </Badge>
                      )}
                      {filters.startDate && (
                        <Badge bg="warning" className="d-flex align-items-center py-2 px-3 rounded-pill">
                          From: {filters.startDate}
                          <button 
                            className="btn-close btn-close-white ms-2" 
                            style={{ fontSize: '0.6em' }}
                            onClick={() => clearFilter('startDate')}
                            aria-label="Remove start date filter"
                          ></button>
                        </Badge>
                      )}
                      {filters.endDate && (
                        <Badge bg="info" className="d-flex align-items-center py-2 px-3 rounded-pill">
                          To: {filters.endDate}
                          <button 
                            className="btn-close btn-close-white ms-2" 
                            style={{ fontSize: '0.6em' }}
                            onClick={() => clearFilter('endDate')}
                            aria-label="Remove end date filter"
                          ></button>
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Chart */}
          <Col xs={12}>
            <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
              <Card.Header className="bg-white border-bottom-0 pt-3 pb-0">
                <h6 className="fw-bold mb-3">
                  {filters.departments 
                    ? `${filters.departments} Department - Audit Score Performance`
                    : viewMode === 'department' 
                      ? 'Department-wise Audit Score Performance' 
                      : 'Branch-wise Audit Score Performance'}
                </h6>
              </Card.Header>
              <Card.Body>
                {currentData.length > 0 ? (
                  <div style={{ width: '100%', height: 400 }}>
                    <ResponsiveContainer>
                      <BarChart
                        data={currentData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis 
                          dataKey={viewMode === 'department' ? "departmentName" : "branchName"} 
                          angle={-45} 
                          textAnchor="end"
                          height={70}
                          tick={{ fontSize: 12, fill: '#6c757d' }}
                          tickLine={false}
                        />
                        <YAxis 
                          domain={[0, 100]}
                          tickFormatter={(value) => `${value}%`}
                          tick={{ fontSize: 12, fill: '#6c757d' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ paddingTop: 15 }} />
                        <Bar 
                          name="Average Score (%)" 
                          dataKey="avgScore" 
                          barSize={100}
                          animationDuration={1500}
                          animationEasing="ease-out"
                        >
                          {
                            currentData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={getScoreColor(entry.avgScore)}
                                stroke={getScoreColor(entry.avgScore)}
                                strokeWidth={1}
                              />
                            ))
                          }
                          <LabelList 
                            dataKey="avgScore" 
                            position="top" 
                            formatter={(value) => `${value}%`}
                            style={{ fontSize: '12px', fill: '#344767', fontWeight: 'bold' }} 
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-3 bg-light p-3 rounded-circle d-inline-flex align-items-center justify-content-center">
                      <FaChartBar size={32} className="text-secondary" />
                    </div>
                    <h6 className="text-muted">No audit progress data available</h6>
                    <p className="text-muted small">
                      {hasActiveFilters
                        ? 'No data found for the selected filters. Try adjusting your filters to see more data.'
                        : 'Complete some audits to see progress analytics here.'}
                    </p>
                    {hasActiveFilters && (
                      <button 
                        className="btn btn-outline-primary btn-sm mt-2"
                        onClick={clearAllFilters}
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Performance Legend */}
          {currentData.length > 0 && (
            <Col xs={12}>
              <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                <Card.Body>
                  <h6 className="fw-bold mb-3 d-flex align-items-center">
                    <FaInfoCircle className="me-2 text-primary" /> Performance Legend
                  </h6>
                  <Row className="g-3">
                    <Col xs={12} sm={4}>
                      <div className="d-flex align-items-center p-2 border border-light rounded bg-light bg-opacity-50">
                        <div 
                          className="me-3" 
                          style={{ 
                            width: '24px', 
                            height: '24px', 
                            backgroundColor: '#28a745', 
                            borderRadius: '6px' 
                          }}
                        ></div>
                        <div>
                          <div className="fw-bold text-success">Excellent</div>
                          <div className="small text-muted">80-100%</div>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={4}>
                      <div className="d-flex align-items-center p-2 border border-light rounded bg-light bg-opacity-50">
                        <div 
                          className="me-3" 
                          style={{ 
                            width: '24px', 
                            height: '24px', 
                            backgroundColor: '#ffc107', 
                            borderRadius: '6px' 
                          }}
                        ></div>
                        <div>
                          <div className="fw-bold text-warning">Good</div>
                          <div className="small text-muted">60-79%</div>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={4}>
                      <div className="d-flex align-items-center p-2 border border-light rounded bg-light bg-opacity-50">
                        <div 
                          className="me-3" 
                          style={{ 
                            width: '24px', 
                            height: '24px', 
                            backgroundColor: '#dc3545', 
                            borderRadius: '6px' 
                          }}
                        ></div>
                        <div>
                          <div className="fw-bold text-danger">Needs Improvement</div>
                          <div className="small text-muted">0-59%</div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      )}
    </div>
  );
}

export default FactoryAuditProgress;